import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import { useAccountingPeriods } from '../../../features/accounting/client';

import { useState, useEffect } from 'react';
import { useCommissionLedger } from '../../../features/accounting/client';
import { Money } from '../../../components/ui/Money';
import { Skeleton } from '../../../components/ui/Skeleton';

export const Route = createFileRoute('/_auth/accounting/commission')({
  component: CommissionLedgerPage,
});

function CommissionLedgerPage() {
  const [dateRange, setDateRange] = useState({ fromDate: '', toDate: '' });
  const { data: response, isLoading, isError, refetch } = useCommissionLedger(dateRange);
  const { data: periods } = useAccountingPeriods();
  const entries = response?.entries ?? [];

  // Default to current open period
  useEffect(() => {
    if (periods && periods.length > 0 && !dateRange.fromDate && !dateRange.toDate) {
      const openPeriod = periods.find(p => p.status === 'open');
      if (openPeriod) {
        setDateRange({ fromDate: openPeriod.startDate, toDate: openPeriod.endDate });
      }
    }
  }, [periods, dateRange.fromDate, dateRange.toDate]);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader title="Commission Register" subtitle="Track commission calculations and payouts." />
      <div className="flex-1 w-full min-h-0">
        {(() => {
          if (isLoading) return <Skeleton rows={5} />;
          if (isError) return (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load commission ledger</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          );
          return (
            <>
              {periods && periods.length === 0 && (
                <div className="mb-6 p-4 bg-warning-50 border border-warning-200 text-warning-800 rounded-md text-sm">
                  No active accounting period found. Commission entries are populated once accounting periods and transactions are recorded.
                </div>
              )}
              <div className="flex gap-4 mb-6 max-w-2xl">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">From Date</label>
                  <Input 
                    type="date" 
                    className="w-full border border-surface-3 rounded p-2"
                    value={dateRange.fromDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateRange({ ...dateRange, fromDate: e.target.value })}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">To Date</label>
                  <Input 
                    type="date" 
                    className="w-full border border-surface-3 rounded p-2"
                    value={dateRange.toDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateRange({ ...dateRange, toDate: e.target.value })}
                  />
                </div>
              </div>
              <div className="bg-surface-0 border border-surface-2 rounded-lg overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-surface-1 border-b border-surface-2">
                    <tr>
                      <th className="py-3 px-4 font-medium text-sm">Date</th>
                      <th className="py-3 px-4 font-medium text-sm">Group</th>
                      <th className="py-3 px-4 font-medium text-sm">Narration</th>
                      <th className="py-3 px-4 font-medium text-sm text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-text-muted">No commission entries found</td>
                      </tr>
                    ) : (
                      entries.map((entry) => (
                        <tr key={entry.id} className="border-b border-surface-2 hover:bg-surface-50">
                          <td className="py-3 px-4">{new Date(entry.date).toLocaleDateString()}</td>
                          <td className="py-3 px-4">{entry.sourceId}</td>
                          <td className="py-3 px-4">{entry.narration}</td>
                          <td className="py-3 px-4 text-right text-success-600">
                            <Money paise={entry.creditPaise} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
